// MenuGraph.js
import React, {useEffect, useState} from "react";
import "../style/MenuGraph.css";

const formatPercent = (value) => `${(value * 100).toFixed(1)}%`;

// ContextLink's own bookkeeping, written into the KG's nodes and edges --
// meaningless to users, so never listed in Node/Edge details. Nodes carry the
// Louvain cluster ids; edges the weight components (constant/local/global)
// and every combined weight type ContextLink can compute.
const CONTEXTLINK_PROPERTIES = new Set([
  'clusters', 'community', 'intermediateCommunities',
  'constant', 'local', 'global',
  'balanced', 'alphaPlus', 'betaPlus', 'gammaPlus',
  'alphaPlusPlus', 'betaPlusPlus', 'gammaPlusPlus',
]);
const visibleProperties = (items) =>
  Object.entries(items).filter(([key]) => !CONTEXTLINK_PROPERTIES.has(key));

// Which of the two plausibility-region thresholds a score is numerically
// closer to, and by how much -- gives a sense of margin, not just the color
// the edge is already drawn in (see getConfidenceColor in LoadGraph.js).
const getClosestThreshold = (confidence, medianPositive, medianNegative) => {
  const distanceToPositive = Math.abs(confidence - medianPositive);
  const distanceToNegative = Math.abs(confidence - medianNegative);
  return distanceToPositive <= distanceToNegative
    ? { label: 'Plausible', value: medianPositive, distance: distanceToPositive, color: 'green' }
    : { label: 'Implausible', value: medianNegative, distance: distanceToNegative, color: 'red' };
};


const MenuGraph = ({ isOpen, onClose, menuItems, mySelectedMenuNode, mySelectedMenuEdge, setMySelectedMenuNode, setMySelectedMenuEdge, myMenuGraph, setMyMenuGraph }) => {

  const [showNodes, setShowNodes] = useState(true);   //control variable to show neighbors or complete graph
  const [showEdges, setShowEdges] = useState(true);   //control variable to show extreme or complete graph
  const [openSeq, setOpenSeq] = useState(false);

//- - - - - - - - - - - - - - - - -HANDLE NEIGHBORS- - - - - - - - - - - - - - - - - - - - - - - - - - -  
  const handleNeighbors = () => {
    const updatedGraph = myMenuGraph.copy(); //copy of current graph
    if (mySelectedMenuNode) {
      setShowNodes(false);    //change the labelButton
      const neighbors = myMenuGraph.neighbors(mySelectedMenuNode);    //get the neighbors node
      updatedGraph.forEachNode((node, attributes) => {
        //set visible only the selected node and its neighbors
        attributes.hidden = mySelectedMenuNode !== node && !neighbors.includes(node);
      });
    } else if (mySelectedMenuEdge){
      setShowEdges(false);    //change the labelButton
      updatedGraph.forEachNode((node, attributes) => {
        //set visible only the extreme node of edge selected (and obviously the edge)
        const isNodeInSelectedEdge = node === updatedGraph.source(mySelectedMenuEdge) || node === updatedGraph.target(mySelectedMenuEdge);
        attributes.hidden = !isNodeInSelectedEdge;
      });
    }
    setMyMenuGraph(updatedGraph);
  }
  
//- - - - - - - - - - - - - - - - -HANDLE GRAPH- - - - - - - - - - - - - - - - - - - - - - - - - - -  
  const handleGraph = () =>{
    setShowNodes(true);     //change the labelButton
    setShowEdges(true);     //change the labelButton
    if (myMenuGraph) {
      const updatedGraph = myMenuGraph.copy(); //copy of graph
      updatedGraph.forEachNode((node, attributes) => {
        //sets the hidden attribute to false for all nodes that are not close
        attributes.hidden = false;
      });
      setMyMenuGraph(updatedGraph);
    }
  }

//- - - - - - - - - - - - - - - - -HANDLE HIDE- - - - - - - - - - - - - - - - - - - - - - - - - - -  
  const handleHide = () =>{
    const updatedGraph = myMenuGraph.copy();        //copy of graph
    if (mySelectedMenuNode) {     
      updatedGraph.dropNode(mySelectedMenuNode);      //delete selected node from updateGraph
      setMySelectedMenuNode(null);        //anyone node selected
    }else if (mySelectedMenuEdge) {     
      if (updatedGraph.getEdgeAttribute(mySelectedMenuEdge,'type') === 'line') {
        const sourceNode = updatedGraph.source(mySelectedMenuEdge);
        const targetNode = updatedGraph.target(mySelectedMenuEdge);
        updatedGraph.dropEdge(mySelectedMenuEdge);
        const newEdge = updatedGraph.edge(targetNode, sourceNode);
        updatedGraph.dropEdge(newEdge);
      } else {
        updatedGraph.dropEdge(mySelectedMenuEdge);
      }
      setMySelectedMenuEdge(null);
    }
    setMyMenuGraph(updatedGraph);       //refresh graph
    onClose();    //close menu attribute
  }

  return (
    
    <div className={`menu-node ${isOpen ? "open" : ""}`}>
      
      {/*SELECTED NODE*/}
      {mySelectedMenuNode && (
        <>
          <h3 style={{ margin: 0, padding: 0 }}>Node details</h3>
          {visibleProperties(menuItems).map(([key, value]) => (
            <p key={key}>
              {key === 'name' ? (
                <>
                  <strong>{key}</strong>
                  <br />
                  <a href={value.replace(/[<>]/g, '')}>{value.replace(/[<>]/g, '')}</a>
                </>
              ) : key === 'family' ? (  // Qui cambia "family" con "Type"
                <>
                  <strong>Type</strong>
                  <br />
                  {value.join(' | ').replace(/_/g, ' ')}
                </>
              ) : key === 'Synonym' ? (
                <>
                  <strong>Synonyms</strong>
                  <br />
                  {value.join(' | ')} 
                </>
              ) : key === 'Sequence' ? (
                <>
                  <strong>Sequence</strong>
                  <br />

                  {openSeq === false ?
                  (
                    <button className="btn btn-outline-secondary toggle" onClick={() => setOpenSeq(!openSeq)}>
                      {`${value.slice(0, 30)}...`}
                    </button>
                  ) : 
                    (
                      <button className="btn btn-outline-secondary toggle" onClick={() => setOpenSeq(!openSeq)}>
                        {value}
                      </button>
                    )
                  }
                
                </>
              ) : key !== 'x' && key !== 'y' && key !== 'size' && key !== 'color' && key !== 'hidden' && key !== 'label' && key!== 'URI' ? (
                <>
                  <strong>{key.split('_').join(' ')}</strong>
                  <br />
                  {value}
                </>
              ) : key == 'URI' ? (
                <>
                  <strong>{key}</strong>
                  <br />
                  <a href={value} target='_blank'>{value}</a>
                </>
              ) : null}
            </p>
          ))}
          <button type='button' className="btn-close close" onClick={onClose} aria-label="Close" />
          {/* {showNodes ? (
            <button className="showNodes" onClick={handleNeighbors}>
              <span>Show neighbors</span>
            </button>
          ) : (
            <button className="showNodes" onClick={handleGraph}>
              <span>Show graph</span>
            </button>
          )}
          <button className="hideNode" onClick={handleHide}>
            <span>Hide node</span>
          </button> */}
        </>
      )}
{/* SELECTED EDGE */}
{mySelectedMenuEdge && (
  <>
    <h3 style={{ margin: 0, padding: 0 }}>Edge details</h3><br />
    <>
      <strong>Source:</strong>
      <br />
      {myMenuGraph.getNodeAttribute(myMenuGraph.source(mySelectedMenuEdge), 'label')}
    </>
    <br /><br />
    <>
      <strong>Target:</strong>
      <br />
      {myMenuGraph.getNodeAttribute(myMenuGraph.target(mySelectedMenuEdge), 'label')}
    </>
    {menuItems['_median_positive'] !== undefined && menuItems['_median_positive'] !== -2 &&
     menuItems['_median_negative'] !== undefined && menuItems['_median_negative'] !== -2 && (() => {
      const closest = getClosestThreshold(menuItems['_confidence'], menuItems['_median_positive'], menuItems['_median_negative']);
      return (
        <p>
          <strong>Plausibility Score</strong>
          <br />
          {formatPercent(menuItems['_confidence'])}
          <span style={{ color: closest.color }}>
            {' '}({formatPercent(closest.distance)} from {closest.label} threshold at {formatPercent(closest.value)})
          </span>
        </p>
      );
    })()}
    {visibleProperties(menuItems).map(([key, value]) => (
      <p key={key}>
        {key === 'Tipology' ? (  // Sostituisci 'Tipology' con 'Type'
          <>
            <strong>Type</strong>
            <br />
            {value}
          </>
        ) : key !== 'x' && key !== 'y' && key !== 'size' && key !== 'color' && key !== 'hidden' &&
           key !== 'parallelMinIndex' && key !== 'declined' && key !== 'accept' && key !== 'type' &&
           key !== 'parallelIndex' && key !== 'parallelMaxIndex' && key !== 'curvature' &&
           key !== '_confidence' && key !== '_base' && key !== '_max' && key !== '_comb' &&
           key !== '_median_positive' && key !== '_median_negative' ? ( // Rimuovi 'type' e 'declined'; plausibility score shown above instead
          <>
            <strong>{key}</strong>
            <br />
            {value}
          </>
        ) : null}
      </p>
    ))}
    <button type='button' className="btn-close close" onClick={onClose} aria-label="Close" />
    {/* {showEdges ? (
      <button className="showNodes" onClick={handleNeighbors}>
        <span>Show extremes</span>
      </button>
    ) : (
      <button className="showNodes" onClick={handleGraph}>
        <span>Show graph</span>
      </button>
    )}
    {myMenuGraph.getEdgeAttribute(mySelectedMenuEdge, '_confidence') === undefined ? (
      <button className="hideNode" onClick={handleHide}>
        <span>Hide edge</span>
      </button>
    ) : (
      console.log("Errore")
    )} */}
  </>
)}

     
          
    </div>
  );
};

export default MenuGraph;